import { useMethod } from '../../../../lib/hooks';
import { scopeMembershipFingerprint } from '../../lib/scopeFilter';
import type { ShiftFilter } from '../../types';
import { NEAR_DAYS } from './horizonHighlights';
import type { HorizonBookingsPayload, HorizonPayload } from './horizonTypes';

function shiftArg(shifts: ShiftFilter | null | undefined): string[] | null {
  return shifts && shifts.length ? shifts : null;
}

/**
 * Cache-only SWR segment. `group:current-selection` is a stable filter key
 * while its restaurant list changes in localStorage — without the member
 * fingerprint, Apply reuses the previous Horizon payload and looks like a
 * no-op. Same helper Performance already uses.
 */
export function scopeKeyExtra(regionFilter: string | null): string {
  const membership = scopeMembershipFingerprint(regionFilter);
  return membership
    ? `${regionFilter}|${membership}`
    : regionFilter || 'all';
}

export function useHorizon(
  restaurantId: string | undefined,
  regionFilter: string | null,
  enabled: boolean,
  shifts: ShiftFilter | null = null,
  /** Growth's demo group: the demo's forward book, never the person's venues. */
  demo: string | null = null,
) {
  const scopeArg =
    !regionFilter || regionFilter === 'all' ? null : regionFilter;

  return useMethod<HorizonPayload>(
    'opsCommand/getHorizon',
    restaurantId && enabled ? (demo ? [restaurantId, null, null] : [restaurantId, scopeArg, shiftArg(shifts)]) : null,
    demo
      ? { keepPreviousData: false, demo }
      : {
          refreshInterval: 15 * 60_000,
          // A scope change must not keep showing the previous fleet.
          keepPreviousData: false,
          keyExtra: `${scopeKeyExtra(regionFilter)}|${shiftArg(shifts)?.join(',') || 'all'}`,
        },
  );
}

/**
 * Reservation detail for one focused outlet across the near window.
 *
 * Only fetches once an outlet is focused: guest tags come from the aleno
 * GraphQL API at one call per outlet per day, so a fleet-wide fetch would
 * stampede the gateway.
 */
export function useHorizonBookings(
  restaurantId: string | undefined,
  outletId: string | null,
  startDate: string | null,
  regionFilter: string | null,
  enabled: boolean,
  shifts: ShiftFilter | null = null,
) {
  const scopeArg = !regionFilter || regionFilter === 'all' ? null : regionFilter;

  return useMethod<HorizonBookingsPayload>(
    'opsCommand/getHorizonBookings',
    restaurantId && outletId && enabled
      ? [restaurantId, outletId, startDate, NEAR_DAYS, scopeArg, shiftArg(shifts)]
      : null,
    {
      refreshInterval: 5 * 60_000,
      keepPreviousData: true,
      keyExtra: `${scopeKeyExtra(regionFilter)}|${outletId || 'none'}|${startDate || 'today'}|${shiftArg(shifts)?.join(',') || 'all'}`,
    },
  );
}
